import React from 'react';
import { Linking, Platform } from 'react-native';

import { cn } from '../utils/cn';
import ThemedText from './ThemedText';

interface TextLinkProps {
  href: string;
  children: React.ReactNode;
  onPress?: () => void;
  testID?: string;
}

/**
 * Underlined navigation link.
 *
 * On web, react-native-web renders a Text with `accessibilityRole='link'` and
 * an `href` prop as a real `<a href='...'>` in the DOM. That restores every
 * affordance a native anchor gives you and a Pressable does not:
 *   - hover URL preview in the browser status bar
 *   - right-click / cmd-click / middle-click to open in a new tab
 *   - "Copy link address"
 *   - correct screen-reader role (link, not button)
 *
 * Client-side navigation still happens via the caller's `onPress` (usually
 * `router.push(...)`); react-native-web preventDefaults the browser's default
 * navigation when onPress is set. External links without an onPress fall back
 * to a full-page nav via `window.location.href`.
 *
 * Native platforms honor `accessibilityRole` for VoiceOver / TalkBack; `href`
 * is a no-op there and `Linking.openURL` is invoked from `onPress` for
 * external URLs.
 */
export const TextLink: React.FC<TextLinkProps> = ({ href, children, onPress, testID }) => {
  const handlePress = (event?: { preventDefault?: () => void }) => {
    // React Native Web renders Text with accessibilityRole='link' + href as
    // <a href="...">. RN Web does NOT automatically preventDefault when
    // onPress is set, so without this the browser follows the anchor AND
    // we call router.push — a double navigation where the browser's full
    // page reload wins. That drops SPA-only URL state, including the
    // ?branch_name= param that the spa-router uses to serve the correct
    // branch bundle on staging, so any click on a branch build blows the
    // user back to main.
    //
    // Modifier clicks (cmd/ctrl/shift/middle-click) never reach onPress in
    // RN Web — those fall through to the browser's native anchor behavior,
    // so preventDefault-ing here doesn't break "open in new tab" and
    // friends.
    if (Platform.OS === 'web' && typeof event?.preventDefault === 'function') {
      event.preventDefault();
    }

    if (onPress) {
      onPress();
      return;
    }
    if (Platform.OS === 'web') {
      window.location.href = href;
    } else if (href.startsWith('http') || href.startsWith('mailto:')) {
      Linking.openURL(href);
    }
  };

  return (
    <ThemedText
      type='bodySmall'
      className={cn(
        'text-login-purple-90 underline font-medium',
        'active:opacity-80 hover:opacity-80',
        Platform.OS === 'web' && 'cursor-pointer',
      )}
      accessibilityRole='link'
      onPress={handlePress}
      testID={testID}
      // @ts-expect-error `href` is a react-native-web-only extension of Text;
      // it is not declared in react-native's TextProps but is picked up in the
      // DOM as an <a href="..."> attribute.
      href={href}
    >
      {children}
    </ThemedText>
  );
};
